/**
 * AdminImpersonationBanner — shown globally when a super-admin is viewing
 * the platform as another (scoped) admin. Wired by `AdminScopesPage` when
 * the operator clicks "View as" on a scope row.
 *
 * Storage contract:
 *   localStorage.admin_impersonation = JSON.stringify({
 *     original_token, target_id, target_email, target_name, started_at,
 *   })
 *   localStorage.admin_token = <the impersonated token>  (set by the click handler)
 *
 * Clicking "End session" restores `admin_token` from `original_token` and
 * clears the impersonation key, then hard-reloads so every cached query
 * re-fetches with the new identity.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ShieldAlert, LogOut } from 'lucide-react';

const KEY = 'admin_impersonation';

export const AdminImpersonationBanner = () => {
  const [sess, setSess] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_e) {
      return null;
    }
  });

  // Listen for cross-tab impersonation changes (storage event)
  useEffect(() => {
    const onStorage = () => {
      try {
        const raw = localStorage.getItem(KEY);
        setSess(raw ? JSON.parse(raw) : null);
      } catch (_e) {
        setSess(null);
      }
    };
    window.addEventListener('storage', onStorage);
    // Same-tab updates: poll a few times then settle (low cost; rare event)
    const t = setInterval(onStorage, 2000);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(t);
    };
  }, []);

  const end = () => {
    if (sess?.original_token) {
      localStorage.setItem('eventflow_token', sess.original_token);
      localStorage.setItem('admin_token', sess.original_token);
      localStorage.setItem('token', sess.original_token);
    }
    localStorage.removeItem(KEY);
    setSess(null);
    // Hard reload so every React Query cache flushes and re-fetches as the
    // original super-admin user. Cheaper than walking every store manually.
    window.location.href = '/admin/rbac/scopes';
  };

  if (!sess) return null;

  return (
    <div
      className="sticky top-0 z-50 bg-gradient-to-r from-amber-500 to-rose-500 text-white shadow-lg"
      data-testid="admin-impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3">
        <ShieldAlert className="h-5 w-5 flex-shrink-0" />
        <div className="flex-1 text-sm">
          <span className="font-bold">Viewing as:</span>{' '}
          <span>{sess.target_name || sess.target_email || sess.target_id}</span>
          <span className="mx-2 opacity-70">·</span>
          <span className="text-xs opacity-90">
            Started {sess.started_at ? new Date(sess.started_at).toLocaleTimeString() : 'now'} — every action is recorded under the original super-admin
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={end}
          className="bg-white/15 hover:bg-white/25 border-white/30 text-white"
          data-testid="admin-impersonation-end-btn"
        >
          <LogOut className="h-4 w-4 mr-1" /> End session
        </Button>
      </div>
    </div>
  );
};

export default AdminImpersonationBanner;
